import {PRE_ORDER_MARKER} from './add-to-cart';
import PreOrder from '../pre-order';
import OriginalButton from './original-button';
import AppLibrary from '../app-library';

export const LOCATION_LIST_SELECTION_ALL = 'All';
export const SOLD_OUT_CLASS = 'pre-order-closed-message';
const PRE_ORDER_WRAPPER = 'spurit-po-wrapper';

const DISABLED_PRE_ORDER_STATUS_SHOW_MESSAGE = 'showMessage';

const ERROR_LOG_TIMEOUT = 5000;

export class PreOrderButton {
  /**
   * @type {AppConfig}
   */
  #config;

  /**
   * @type {AppLibrary}
   */
  #library;

  /**
   * @type {OriginalButton}
   */
  #buttonOrig;

  /**
   * @type {OriginalButton|null}
   */
  #buttonDefaultBuyNow = null;

  /**
   * @type {HTMLElement|null}
   */
  #buttonNew = null;

  /**
   * @type {HTMLElement|null}
   */
  #message = null;

  /**
   * @type {{id:number, title:string, inventory_quantity:number, inventory_management:string, inventory_policy:string}|null}
   */
  #variant;

  /**
   * @type {{config, date_start, date_stop, is_active: boolean}}
   */
  #settings;

  /**
   * @type {PreOrder}
   */
  #preOrder;

  /**
   * @type {number}
   */
  #lastErrorTimsMs = 0;

  /**
   * @param {HTMLElement} buttonOrig
   * @param {AppConfig} config
   * @param {AppLibrary} library
   * @param {string} page
   * @param {PreOrder} preOrder
   */
  constructor(buttonOrig, config, library, page, preOrder) {
    this.#buttonOrig = new OriginalButton(buttonOrig, config.settings());

    if (page === 'products') {
      let button = Spurit.global.utils.nearest(buttonOrig, config.customization().buyNowSelector);
      if (button) {
        this.#buttonDefaultBuyNow = new OriginalButton(button, config.settings());
      }
    }
    this.#config = config;
    this.#library = library;
    this.#preOrder = preOrder;
  }

  /**
   *
   */
  checkAndDisplay() { //todo refactor me
    let buttonElement = this.#buttonOrReplacingElement;
    if (!buttonElement) {
      return;
    }
    Spurit.global.atc.lineItemByTargetElement(buttonElement, lineItem => {
      if (this.#variant === null && !lineItem) {
        return;
      }

      if (!lineItem) {
        if (this.#errorLogAllowed()) {
          AppLibrary.consoleLog(
              {error: 'Can\'t find line-item for given button', buttonElement},
              'warn'
          );
        }

        this.#lastErrorTimsMs = (new Date()).getTime();
        this.#variant = null;
        this.revertAllChanges();
        return;
      }

      if (typeof this.#variant !== 'undefined' && this.#variant !== null && this.#variant.id === lineItem.id) {
        return;
      }

      this.#variant = this.#config.snippet().getVariantDetails(lineItem.id);
      if (!this.#variant) {
        if (this.#errorLogAllowed()) {
          AppLibrary.consoleLog(`Can't find variant details by ID ${lineItem.id}`, 'warn');
        }

        this.#lastErrorTimsMs = (new Date()).getTime();
        return;
      }

      this.#settings = this.#config.getSettingsByVariantId(this.#variant.id);
      if (!this.#settings) {
        if (this.#errorLogAllowed()) {
          AppLibrary.consoleLog(`No pre-order settings for variant ${this.#variant.id}`);
        }

        this.#lastErrorTimsMs = (new Date()).getTime();
        return;
      }

      this.#removePreOrderElements();

      let status = this.#preOrder.preorderStatusForVariant(this.#variant, this.#settings);
      if (status === PreOrder.PREORDER_STATUS_ENABLED) {
        this.#hideOriginalButton();
        this.#showNewButton();
      } else if (status === PreOrder.PREORDER_STATUS_SOLD_OUT) {
        this.#hideOriginalButton();
        this.#showSoldOutMessage();
      } else if (status === PreOrder.PREORDER_STATUS_DISABLED) {
        this.#showOriginalButton();
      }
    });
  }

  /**
   *
   */
  revertAllChanges() {
    this.#removePreOrderElements();
    this.#showOriginalButton();
  }

  /**
   * @returns {{id: number, title: string, inventory_quantity: string, inventory_management: string, inventory_policy: string}}
   */
  get variant() {
    return this.#variant;
  }

  /**
   * @returns {{config, date_start, date_stop}}
   */
  get settings() {
    return this.#settings;
  }

  /**
   * @returns {HTMLElement}
   * @deprecated (unused, suggest tro delete)
   */
  get buttonOrig() {
    return this.#buttonOrig.element;
  }

  /**
   * @return {HTMLElement}
   */
  get buttonNew() {
    return this.#buttonNew;
  }

  /**
   * @return {HTMLElement|null}
   */
  get #buttonOrReplacingElement() {
    let buttonElement = this.#buttonOrig.notInDocument ? (this.#buttonNew || this.#message) : this.#buttonOrig.element;
    if (!document.body.contains(buttonElement)) {
      return null;
    }
    return buttonElement;
  }

  /**
   *
   * @returns {number|boolean}
   */
  #errorLogAllowed() {
    return this.#lastErrorTimsMs && (new Date()).getTime() - this.#lastErrorTimsMs < ERROR_LOG_TIMEOUT;
  }

  /**
   *
   */
  #showNewButton() {
    const parentChildNodes = Array.from(this.#buttonOrig.container.parentElement.childNodes);
    if (parentChildNodes.some(node => node.classList && node.classList.contains(PRE_ORDER_WRAPPER))) {
      return;
    }

    this.#buttonNew = this.#renderNewButton();
    this.#buttonOrig.insertBefore(this.#buttonNew);
  }

  /**
   *
   */
  #showSoldOutMessage() {
    if (!this.#settings) {
      return;
    }

    const parentChildNodes = Array.from(this.#buttonOrig.parent.parentElement.childNodes);
    if (parentChildNodes.some(node => node.classList && node.classList.contains(SOLD_OUT_CLASS))) {
      return;
    }

    this.#message = document.createElement("span");
    this.#message.className = SOLD_OUT_CLASS;
    this.#message.innerHTML = this.#settings.config.closedPreOrderMessage;
    this.#buttonOrig.insertBefore(this.#message);

    // Fix for some strange themes where this element receives a style "font-size:0"
    if (window.getComputedStyle) {
      let fs = parseInt(window.getComputedStyle(this.#message).fontSize.replace(/[^\d]+/g, ''));
      if (!fs) {
        this.#message.style.fontSize = '1rem';
      }
    }
  }

  /**
   * @return {HTMLElement}
   */
  #renderNewButton() {
    const div = document.createElement('div');
    div.classList.add(PRE_ORDER_WRAPPER);

    const view = {
      buttonText: this.#getButtonText(),
      buttonMessage: this.#getButtonMessage(),
      messageDirectionClassName: this.#getButtonMessageDirection(),
      messageVisibilityClassName: this.#getButtonMessageVisibility(),
      buttonMessageShown: !!(this.#getButtonMessage() && this.#getButtonMessage().length),
      customStyle: this.#getButtonCustomStyle()
    };

    div.innerHTML = this.#library.mustache().render(this.#getButtonTemplate(), view);

    return div;
  }

  /**
   * @return {string}
   */
  #getButtonText() {
    return this.#settings.config.buttonText;
  }

  /**
   * @return {string}
   */
  #getButtonCustomStyle() {
    return this.#config.settings().config.design.params.customStyle;
  }

  /**
   * @return {string}
   */
  #getButtonMessage() {
    return this.#settings.config.buttonMessage;
  }

  /**
   * @return {string}
   */
  #getButtonMessageDirection() {
    return this.#config.settings().config.selectedPosition || this.#config.settings().config.design.params.messageDirectionClassName;
  }

  /**
   * @return {string}
   */
  #getButtonMessageVisibility() {
    return this.#config.settings().config.visible || this.#config.settings().config.design.params.messageVisibilityClassName;
  }

  /**
   * @return {string}
   */
  #getButtonTemplate() {
    return this.#config.settings().config.design.template;
  }

  /**
   *
   */
  #hideOriginalButton() {
    this.#buttonOrig.hide();
    if (this.#buttonDefaultBuyNow) {
      this.#buttonDefaultBuyNow.hide();
    }
  }

  /**
   *
   */
  #showOriginalButton() {
    this.#buttonOrig.restore();
    if (this.#buttonDefaultBuyNow) {
      this.#buttonDefaultBuyNow.restore();
    }
  }

  /**
   *
   */
  #removePreOrderElements() {
    if (this.#message && this.#message.parentNode) {
      this.#message.parentNode.removeChild(this.#message);
      this.#message = null;
    }

    if (this.#buttonNew) {
      this.#buttonNew.parentNode.removeChild(this.#buttonNew);
      this.#buttonNew = null;
    }
  }

  /**
   * Used if theme had been changed,
   * but ATC styles not parsed
   * @param {AppConfig} config
   * @returns {boolean}
   */
  static isAtcStyleNotApplied(config) {
    return config.settings().config.design.params.customStyle === false &&
      (config.settings().config.parsedCss === '' || config.settings().config.parsedCss === null) &&
      !config.settings().config.parsedAtcStyle.length;
  }

  /**
   * @param {String} selector
   */
  static cloneStyle(selector) {
    let sourceElement;
    try {
      sourceElement = document.querySelector(selector);
      if (!sourceElement) {
        AppLibrary.consoleLog('No `Add To Cart` button found');
        return;
      }

      const sourceStyle = Object(window.getComputedStyle(sourceElement, ''));
      const atcStyles = AppLibrary.processParsedStyle(sourceStyle);

      let cssStrings = Object.keys(atcStyles).map(key => key + ':' + atcStyles[key] + ';');
      if (!cssStrings.length) {
        return;
      }

      const styleTag = document.createElement('style');
      styleTag.type = 'text/css';
      styleTag.innerHTML = '.' + PRE_ORDER_WRAPPER + '.pre-order-2-button {' + cssStrings.join("\n") + '}';

      document.getElementsByTagName('head')[0].appendChild(styleTag);
    } catch (e) {
      AppLibrary.consoleLog('`Add To Cart` styles could not be cloned');
    }
  }
}